import uuid from 'node-uuid';
import moment from 'moment';
import { get, pickBy, first, isEqual, has } from 'lodash';
import { getPaymentsList } from '../payments/getPaymentsList';
import { getPayeeBySortCodeAccountNumberName, getPayees, isPayee } from '../payees/selectors';
import { isSavingsAccount, canPayBeneficiaries } from '../accounts/selectors';
import { getPaymentType, isChaps, isIFT } from '../modes/selectors';
import { createNewFasterPaymentTemplate, goToPage, pages, setBackPage } from '../page/actions';
import { toggleTemplateMode } from '../templates/toggleTemplateMode';
import { getDate, getPaymentsRef, hasAllRequiredCopFields } from '../../helpers/fieldHelpers';
import { resetChapsDailyLimitAuthorisation } from '../../states/chaps/resetChapsDailyLimitAuthorisation';
import { resetIFTDailyLimitAuthorisation } from '../ift/actions';
import { getPayeeSrdCheck, setTemplateError } from '../payees/actions';
import { formValid } from '../validation/selectors';
import { showAllErrors } from '../validation/actions';
import { SUBMIT_CREATE_TEMPLATE_FAILURE, SUBMIT_CREATE_TEMPLATE_SUCCESS } from '../templates/actions';
import { setSelectedPayee } from '../../managePayee/states/action';
import { getSelectedPayee } from '../../managePayee/states/selectors';
import { getPage } from '../page/selectors';
import { goToEditPayeePage } from '../api/fasterPaymentActions';
import { getPayeeById } from '../commonSelectors/selectors';
import { chapsDeletePayment } from '../chaps/actions';
import { removePayment as removeBacsCreditPayment } from '../bacsCredit/actions';
import { removePayment as removeBacsDebitPayment } from '../bacsDebit/actions';
import { setCOPBeneficiaryId } from '../../cop/states/actions';
import { TYPE_BACS_CREDIT, TYPE_BACS_DEBIT, TYPE_CHAPS, TYPE_FASTER_PAYMENTS } from '../../config';

export const INIT_PAYMENTS = 'INIT_PAYMENTS';
export const ADD_PAYMENT = 'ADD_PAYMENT';
export const ADD_MULTIPLE_PAYMENTS = 'ADD_MULTIPLE_PAYMENTS';
export const UPDATE_TO_ACCOUNT = 'UPDATE_TO_ACCOUNT';
export const UPDATE_FROM_ACCOUNT = 'UPDATE_FROM_ACCOUNT';
export const UPDATE_DATE = 'UPDATE_DATE';
export const UPDATE_REFERENCE = 'UPDATE_REFERENCE';
export const UPDATE_TYPE = 'UPDATE_TYPE';
export const REMOVE_PAYMENT = 'REMOVE_PAYMENT';
export const UPDATE_AMOUNT = 'UPDATE_AMOUNT';
export const UPDATE_FREQUENCY = 'UPDATE_PAYMENT_FREQUENCY';
export const UPDATE_STOP_PAYMENT_OPTION = 'UPDATE_STOP_PAYMENT_OPTION';
export const UPDATE_STOP_PAYMENT_ON_NUMBER = 'UPDATE_STOP_PAYMENT_ON_NUMBER';
export const UPDATE_STOP_PAYMENT_ON_DATE = 'UPDATE_STOP_PAYMENT_ON_DATE';
export const COPY_DATE_TO_ALL = 'COPY_DATE_TO_ALL';
export const COPY_REF_TO_ALL = 'COPY_REF_TO_ALL';
export const UPDATE_ALL_DATES = 'UPDATE_ALL_DATES';
export const UPDATE_ALL_FROM_ACCOUNTS = 'UPDATE_ALL_FROM_ACCOUNTS';
export const UPDATE_ALL_PAYMENT_DATES = 'UPDATE_ALL_PAYMENT_DATES';
export const RESET_PAYMENT_AUTHORISATION = 'RESET_PAYMENT_AUTHORISATION';
export const RESET_DAILY_LIMIT_AUTHORISATION = 'RESET_DAILY_LIMIT_AUTHORISATION';
export const SET_AUTHORISERS = 'SET_AUTHORISERS';
export const CLEAR_PAYMENTS = 'CLEAR_PAYMENTS';

export const initialisePayments = (payments) => {
	const p = payments.map((payment) => ({
		...payment,
		date: getDate(payment),
		context_id: payment.id,
		id: uuid.v1(),
		batch_context_id: payment.batchId,
		batchId: payment.batchId && uuid.v1(),
	}));

	const actual = {
		type: INIT_PAYMENTS,
		payments: p,
		meta: {
			allowedDuringApiCall: true,
		},
	};
	return pickBy(actual);
};

export const clearPayments = () => ({
	type: CLEAR_PAYMENTS,
});

export const copyRefToAll = (paymentIds, reference) => ({
	type: COPY_REF_TO_ALL,
	paymentIds,
	reference,
});

export const copyDateToAll = (paymentIds, date) => ({
	type: COPY_DATE_TO_ALL,
	paymentIds,
	date,
});

export const addPayment = (payment) => ({
	payment: {
		...payment,
		date: moment(get(payment, 'date')),
		id: uuid.v1(),
	},
	type: ADD_PAYMENT,
});
export const addMultiplePayments = (payments) => ({
	type: ADD_MULTIPLE_PAYMENTS,
	payments,
});
export const updateToAccount = ({ id, accountId }) => ({
	type: UPDATE_TO_ACCOUNT,
	id,
	accountId,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const updateFromAccount = ({ id, accountId }) => ({
	type: UPDATE_FROM_ACCOUNT,
	id,
	accountId,
});

export const updateDate = ({ id, date, isAuthorisationEdit }) => ({
	type: UPDATE_DATE,
	id,
	date,
	isAuthorisationEdit,
});

export const updateAllDates = ({ date }) => ({
	type: UPDATE_ALL_DATES,
	date,
});

export const updateAllPaymentDates = ({ date }) => ({
	type: UPDATE_ALL_PAYMENT_DATES,
	date,
});

export const updateReference = ({ id, reference }) => ({
	type: UPDATE_REFERENCE,
	id,
	reference,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const updateType = ({ id, paymentType }) => ({
	type: UPDATE_TYPE,
	id,
	paymentType,
});

export const removePayment = ({ id }) => ({
	type: REMOVE_PAYMENT,
	id,
});

export const updateAmount = ({ id, amount }) => ({
	type: UPDATE_AMOUNT,
	id,
	amount,
});

export const updateFrequency = ({ id, frequency }) => ({
	type: UPDATE_FREQUENCY,
	id,
	frequency,
});

export const updateStopPaymentOption = ({ id, stopPaymentOption, stopPaymentOnDate }) => ({
	type: UPDATE_STOP_PAYMENT_OPTION,
	id,
	stopPaymentOption,
	stopPaymentOnDate,
});

export const updateStopPaymentOnNumber = ({ id, stopPaymentOnNumber }) => ({
	type: UPDATE_STOP_PAYMENT_ON_NUMBER,
	id,
	stopPaymentOnNumber,
});

export const updateStopPaymentOnDate = ({ id, stopPaymentOnDate }) => ({
	type: UPDATE_STOP_PAYMENT_ON_DATE,
	id,
	stopPaymentOnDate,
});

export const updateAllFromAccountsAction = ({ accountId }) => ({
	type: UPDATE_ALL_FROM_ACCOUNTS,
	accountId,
});

export const updateAllFromAccounts = (accountId) => (dispatch, getState) => {
	dispatch(updateAllFromAccountsAction({ accountId }));
	const state = getState();
	const payments = getPaymentsList(state);
	if (isSavingsAccount(state, accountId)) {
		dispatch(updateAllDates({ date: moment() }));
	}
	if (!canPayBeneficiaries(state, accountId)) {
		payments.forEach((payment) => {
			const toAccount = get(payment, 'toAccount', '');
			if (isPayee(state, toAccount)) {
				dispatch(updateToAccount({ id: payment.id, accountId: '' }));
			}
		});
	}
};

export const resetPaymentAuthorisation = ({ id }) => ({
	type: RESET_PAYMENT_AUTHORISATION,
	id,
});

export const resetFasterPaymentsDailyLimitAuthorisation = () => ({ type: RESET_DAILY_LIMIT_AUTHORISATION });

export const resetDailyLimitAuthorisation = () => (dispatch, getState) => {
	const state = getState();
	if (isIFT(state)) {
		return dispatch(resetIFTDailyLimitAuthorisation());
	}
	if (isChaps(state)) {
		return dispatch(resetChapsDailyLimitAuthorisation());
	}
	return dispatch(resetFasterPaymentsDailyLimitAuthorisation());
};

export const updatePayment =
	({ id, accountId, reference }) =>
	(dispatch) => {
		dispatch(setCOPBeneficiaryId({ id: accountId }));
		dispatch(updateToAccount({ id, accountId }));
		return dispatch(updateReference({ id, reference }));
	};

export const setPaymentAuthorisers = (authorisers, paymentId) => ({
	type: SET_AUTHORISERS,
	paymentId,
	authorisers,
});

export const createFasterPaymentTemplate = () => (dispatch) => {
	dispatch(createNewFasterPaymentTemplate());
	dispatch(toggleTemplateMode(true));
	dispatch(goToPage(pages.FasterPayments));
	return dispatch(addPayment());
};

export const handlePayeeSrdCheck =
	({
		sortCodeAccountNumber,
		setCopCheckRequired,
		handleSaveTemplate,
		handleContinue,
		saveAsTemplate,
		setShowErrors,
		payeeReference,
	}) =>
	(dispatch, getState) => {
		dispatch(getPayeeSrdCheck(sortCodeAccountNumber)).then(() => {
			const state = getState();

			const selectedPayee = getSelectedPayee(state);

			const toPayee = getPayeeBySortCodeAccountNumberName(
				state,
				selectedPayee.sortCode.replace(/-/g, ''),
				selectedPayee.accountNumber,
				selectedPayee.name
			);

			/**
			 * @todo refactor business logic @paul.curley
			 */
			if (!payeeReference) {
				const paymentsRef = getPaymentsRef(state);
				toPayee.reference = paymentsRef ? paymentsRef : toPayee.reference;
			} else {
				toPayee.reference = payeeReference;
			}

			if (toPayee.isCopCheckRequired) {
				if (hasAllRequiredCopFields(toPayee)) {
					setCopCheckRequired();
				} else {
					dispatch(handleEditPayee(toPayee, true));
				}
			} else {
				dispatch(handlePaymentsContinue({ handleSaveTemplate, handleContinue, saveAsTemplate, setShowErrors }));
			}
		});
	};

export const checkIsPayeeOnSRDList =
	({ sortCodeAccountNumber }) =>
	(dispatch) => {
		dispatch(getPayeeSrdCheck(sortCodeAccountNumber));
	};

export const handlePaymentsContinue =
	({ saveAsTemplate, handleSaveTemplate, handleContinue, setShowErrors }) =>
	(dispatch, getState) => {
		const state = getState();
		const isFormValid = formValid(state);
		dispatch(showAllErrors());

		if (isFormValid) {
			if (saveAsTemplate) {
				return handleSaveTemplate().then((response) => {
					if (response.type === SUBMIT_CREATE_TEMPLATE_SUCCESS) {
						handleContinue();
					} else if (response.type === SUBMIT_CREATE_TEMPLATE_FAILURE) {
						dispatch(setTemplateError(response.payload));
						return { hasError: true };
					}
				});
			}
			handleContinue();
		} else {
			setShowErrors();
		}
	};

export const handleEditPayee =
	(selectedPayee, isCOPEdit = false) =>
	(dispatch, getState) => {
		const state = getState();
		const currentPage = getPage(state);
		dispatch(setSelectedPayee(selectedPayee));
		dispatch(setBackPage(currentPage));
		dispatch(goToEditPayeePage(isCOPEdit));
	};

export const postAddPayeeUpdatePayment = (payment, response, isEdit) => (dispatch, getState) => {
	const state = getState();
	const { reference } = getPayeeById(state, get(payment, 'toAccount'));
	const { id, name, sort_code, account_number } = response;
	const firstPayment = first(getPaymentsList(state)) || {};
	const oldPayee = getPayeeById(state, firstPayment.toAccount);
	const shouldUpdate =
		has(oldPayee, 'to_account') &&
		isEqual(
			{
				name: get(oldPayee, 'to_account.name'),
				sort_code: get(oldPayee, 'to_account.sort_code'),
				account_number: get(oldPayee, 'to_account.account_number'),
			},
			{
				name,
				sort_code,
				account_number,
			}
		);
	if (isEdit || shouldUpdate) {
		return dispatch({
			type: INIT_PAYMENTS,
			payments: [
				{
					date: moment(),
					...payment,
					reference,
					id,
				},
			],
		});
	}

	const newPaymentData = { ...payment, date: moment(), reference, id };
	delete newPaymentData.amount;
	return dispatch({
		type: ADD_PAYMENT,
		payment: newPaymentData,
	});
};

export const handleRemovePayment = (id) => (dispatch, getState) => {
	const state = getState();
	const paymentType = getPaymentType(state);
	switch (paymentType) {
		case TYPE_FASTER_PAYMENTS:
			return dispatch(removePayment({ id }));
		case TYPE_CHAPS:
			return dispatch(chapsDeletePayment(id));
		case TYPE_BACS_CREDIT:
			return dispatch(removeBacsCreditPayment(id));
		case TYPE_BACS_DEBIT:
			return dispatch(removeBacsDebitPayment(id));
		default:
			return;
	}
};
